import {
    useImperativeHandle,
    forwardRef,
    useEffect,
    useState,
    useRef
} from "react";
import {
    type NativeSyntheticEvent,
    type NativeScrollEvent,
    type LayoutChangeEvent,
    type ViewStyle,
    StyleSheet,
    ScrollView,
    Animated,
    Platform,
    View
} from "react-native";
import type {
    EdgeFadeEdge
} from "./internal/edgeFade/type";
import type IScrollAreaProps from "./type";
import type {
    ScrollAreaViewportWebPropsType,
    ScrollAreaWebPropsType,
    ScrollAreaMetrics,
    ScrollAreaOffsets,
    ScrollAreaEdges,
    ScrollAreaAxis
} from "./type";
import {
    getEdgeFadeColor
} from "./internal/edgeFade/stylesheet";
import stylesheet, {
    useStyles
} from "./stylesheet";
import ScrollAreaBar from "./internal/bar";
import EdgeFade from "./internal/edgeFade";
import {
    getHasScrollAreaFocusableContent,
    getScrollAreaPhysicalOffset,
    getIsSameScrollAreaMetrics,
    getIsScrollAreaBarVisible,
    SCROLL_AREA_EMPTY_METRICS,
    getIsSameScrollAreaEdges,
    SCROLL_AREA_EDGE_EPSILON,
    getScrollAreaRadiusStyle,
    getScrollAreaRawOffset,
    getScrollAreaMaxOffset,
    getScrollAreaEdges
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useInteractionState
} from "../../helpers/interaction";
import type {
    RefForwardingComponent
} from "../../types";

const ScrollArea: RefForwardingComponent<ScrollView, IScrollAreaProps> = ({
    onContentSizeChange: onContentSizeChangeProp,
    showsHorizontalScrollIndicator,
    showsVerticalScrollIndicator,
    orientation = "vertical",
    "aria-label": ariaLabel,
    onLayout: onLayoutProp,
    onScroll: onScrollProp,
    contentContainerStyle,
    isFadeVisible = true,
    persistentScrollbar,
    scrollEventThrottle,
    customFadeColor,
    type = "hover",
    customTheme,
    children,
    onFocus,
    onBlur,
    style,
    role,
    ...props
}, ref) => {
    const {
        surfaceColor,
        durations,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const {
        interactionHandlers,
        isFocusVisible
    } = useInteractionState({
        isDescendantFocusIgnored: true,
        onFocus,
        onBlur
    });

    const [
        metrics,
        setMetrics
    ] = useState<ScrollAreaMetrics>(SCROLL_AREA_EMPTY_METRICS);

    const [
        edges,
        setEdges
    ] = useState<ScrollAreaEdges>({
        isBottom: false,
        isStart: false,
        isEnd: false,
        isTop: false
    });

    const [
        isHovered,
        setIsHovered
    ] = useState(false);

    const [
        isScrolling,
        setIsScrolling
    ] = useState(false);

    const [
        hasFocusableContent,
        setHasFocusableContent
    ] = useState(false);

    const scrollRef = useRef<ScrollView>(null);

    const metricsRef = useRef<ScrollAreaMetrics>(SCROLL_AREA_EMPTY_METRICS);

    const offsetsRef = useRef<ScrollAreaOffsets>({
        x: 0,
        y: 0
    });

    const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const horizontalOffset = useRef(new Animated.Value(0)).current;

    const verticalOffset = useRef(new Animated.Value(0)).current;

    const isWeb = Platform.OS === "web";

    const isNestedViewport = !isWeb && orientation === "both";

    const isVerticalEnabled = orientation !== "horizontal";

    const isHorizontalEnabled = orientation !== "vertical";

    const maxX = getScrollAreaMaxOffset(metrics.contentWidth, metrics.layoutWidth);

    const maxY = getScrollAreaMaxOffset(metrics.contentHeight, metrics.layoutHeight);

    const isOverflowing = (isHorizontalEnabled && maxX > SCROLL_AREA_EDGE_EPSILON) || (isVerticalEnabled && maxY > SCROLL_AREA_EDGE_EPSILON);

    const isVerticalBarRendered = isWeb && isVerticalEnabled && showsVerticalScrollIndicator !== false && metrics.layoutHeight > 0 && (type === "always" || maxY > SCROLL_AREA_EDGE_EPSILON);

    const isHorizontalBarRendered = isWeb && isHorizontalEnabled && showsHorizontalScrollIndicator !== false && metrics.layoutWidth > 0 && (type === "always" || maxX > SCROLL_AREA_EDGE_EPSILON);

    const isBarVisible = getIsScrollAreaBarVisible({
        isScrolling,
        isHovered,
        type
    });

    const flattenedStyle = StyleSheet.flatten<ViewStyle>(style);

    const backgroundColor = flattenedStyle?.backgroundColor;

    const fadeColor = getEdgeFadeColor({
        fallbackColor: typeof backgroundColor === "string" ? backgroundColor : colors.content.container[surfaceColor],
        customFadeColor,
        colors
    });

    const {
        horizontalFade: horizontalFadeDynamicStyle,
        verticalFade: verticalFadeDynamicStyle,
        focusRing: focusRingDynamicStyle
    } = useStyles({
        radiusStyle: getScrollAreaRadiusStyle(flattenedStyle),
        isFocusVisible,
        borders,
        colors,
        spaces
    });

    useImperativeHandle(ref, () => scrollRef.current as ScrollView, [
        orientation
    ]);

    const syncScrollState = () => {
        const nextEdges = getScrollAreaEdges({
            metrics: metricsRef.current,
            offsets: offsetsRef.current,
            direction,
            isWeb
        });

        horizontalOffset.setValue(getScrollAreaPhysicalOffset({
            offset: offsetsRef.current.x,
            metrics: metricsRef.current,
            direction,
            isWeb
        }));

        verticalOffset.setValue(offsetsRef.current.y);

        setEdges((current) => getIsSameScrollAreaEdges(current, nextEdges) ? current : nextEdges);
    };

    useEffect(() => {
        syncScrollState();
    }, [
        direction
    ]);

    useEffect(() => {
        if(!isWeb) {
            return;
        }

        setHasFocusableContent(getHasScrollAreaFocusableContent(scrollRef.current?.getScrollableNode()));
    }, [
        metrics.contentHeight,
        metrics.contentWidth
    ]);

    useEffect(() => {
        if(Platform.OS !== "ios" || !isOverflowing || (type !== "auto" && type !== "always")) {
            return;
        }

        scrollRef.current?.flashScrollIndicators();
    }, [
        isOverflowing
    ]);

    useEffect(() => {
        return () => {
            clearTimeout(scrollTimerRef.current);
        };
    }, []);

    const updateMetrics = (next: Partial<ScrollAreaMetrics>) => {
        const nextMetrics = {
            ...metricsRef.current,
            ...next
        };

        if(getIsSameScrollAreaMetrics(metricsRef.current, nextMetrics)) {
            return;
        }

        metricsRef.current = nextMetrics;

        setMetrics(nextMetrics);

        syncScrollState();
    };

    const updateOffsets = (next: Partial<ScrollAreaOffsets>) => {
        offsetsRef.current = {
            ...offsetsRef.current,
            ...next
        };

        syncScrollState();

        if(!isWeb || type === "auto" || type === "always") {
            return;
        }

        setIsScrolling(true);

        clearTimeout(scrollTimerRef.current);

        scrollTimerRef.current = setTimeout(() => {
            setIsScrolling(false);
        }, durations.hoverOpen);
    };

    const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        const {
            contentOffset
        } = event.nativeEvent;

        updateOffsets(isNestedViewport ? {
            y: contentOffset.y
        } : {
            x: contentOffset.x,
            y: contentOffset.y
        });

        onScrollProp?.(event);
    };

    const handleNestedScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        updateOffsets({
            x: event.nativeEvent.contentOffset.x
        });
    };

    const handleLayout = (event: LayoutChangeEvent) => {
        const {
            height,
            width
        } = event.nativeEvent.layout;

        updateMetrics(isNestedViewport ? {
            layoutHeight: height
        } : {
            layoutHeight: height,
            layoutWidth: width
        });

        onLayoutProp?.(event);
    };

    const handleNestedLayout = (event: LayoutChangeEvent) => {
        updateMetrics({
            layoutWidth: event.nativeEvent.layout.width
        });
    };

    const handleContentSizeChange = (width: number, height: number) => {
        updateMetrics(isNestedViewport ? {
            contentHeight: height
        } : {
            contentHeight: height,
            contentWidth: width
        });

        onContentSizeChangeProp?.(width, height);
    };

    const handleNestedContentSizeChange = (width: number) => {
        updateMetrics({
            contentWidth: width
        });
    };

    const handleScrollTo = (axis: ScrollAreaAxis, offset: number, isAnimated: boolean) => {
        const x = axis === "horizontal" ? getScrollAreaRawOffset({
            metrics: metricsRef.current,
            direction,
            offset,
            isWeb
        }) : offsetsRef.current.x;

        scrollRef.current?.scrollTo({
            y: axis === "vertical" ? offset : offsetsRef.current.y,
            animated: isAnimated,
            x
        });
    };

    const handleVerticalScrollTo = (offset: number, isAnimated: boolean) => {
        handleScrollTo("vertical", offset, isAnimated);
    };

    const handleHorizontalScrollTo = (offset: number, isAnimated: boolean) => {
        handleScrollTo("horizontal", offset, isAnimated);
    };

    const getVerticalOffset = () => {
        return offsetsRef.current.y;
    };

    const getHorizontalOffset = () => {
        return getScrollAreaPhysicalOffset({
            offset: offsetsRef.current.x,
            metrics: metricsRef.current,
            direction,
            isWeb
        });
    };

    const handlePointerEnter = () => {
        setIsHovered(true);
    };

    const handlePointerLeave = () => {
        setIsHovered(false);
    };

    const webProps: ScrollAreaWebPropsType = isWeb ? {
        onPointerLeave: handlePointerLeave,
        onPointerEnter: handlePointerEnter
    } : {};

    const webViewportProps: ScrollAreaViewportWebPropsType = isWeb ? {
        tabIndex: hasFocusableContent ? undefined : 0,
        dataSet: interactionHandlers.dataSet
    } : {};

    const isPersistentScrollbar = persistentScrollbar ?? (type === "always" || type === "auto");

    const indicatorProps = isWeb ? {
        showsHorizontalScrollIndicator: false,
        showsVerticalScrollIndicator: false
    } : {
        persistentScrollbar: isPersistentScrollbar,
        showsHorizontalScrollIndicator,
        showsVerticalScrollIndicator
    };

    const renderFade = (edge: EdgeFadeEdge, isEdgeVisible: boolean) => {
        if(!isFadeVisible) {
            return null;
        }

        const isVerticalEdge = edge === "top" || edge === "bottom";

        return <EdgeFade
            style={isVerticalEdge ? [
                stylesheet.verticalFade,
                verticalFadeDynamicStyle
            ] : [
                stylesheet.horizontalFade,
                horizontalFadeDynamicStyle
            ]}
            customTheme={customTheme}
            isVisible={isEdgeVisible}
            color={fadeColor}
            edge={edge}
        />;
    };

    const renderFocusRing = () => {
        if(!isFocusVisible) {
            return null;
        }

        return <View
            style={[
                stylesheet.focusRing,
                focusRingDynamicStyle
            ]}
            aria-hidden={true}
        />;
    };

    const renderViewport = () => {
        if(isNestedViewport) {
            return <ScrollView
                {...props}
                {...indicatorProps}
                role={role ?? (ariaLabel ? "region" : undefined)}
                scrollEventThrottle={scrollEventThrottle ?? 16}
                onContentSizeChange={handleContentSizeChange}
                onFocus={interactionHandlers.onFocus}
                onBlur={interactionHandlers.onBlur}
                style={stylesheet.viewport}
                onLayout={handleLayout}
                onScroll={handleScroll}
                aria-label={ariaLabel}
                ref={scrollRef}
            >
                <ScrollView
                    showsHorizontalScrollIndicator={showsHorizontalScrollIndicator}
                    onContentSizeChange={handleNestedContentSizeChange}
                    scrollEventThrottle={scrollEventThrottle ?? 16}
                    contentContainerStyle={contentContainerStyle}
                    persistentScrollbar={isPersistentScrollbar}
                    onLayout={handleNestedLayout}
                    onScroll={handleNestedScroll}
                    nestedScrollEnabled={true}
                    horizontal={true}
                >
                    {children}
                </ScrollView>
            </ScrollView>;
        }

        return <ScrollView
            {...props}
            {...webViewportProps}
            {...indicatorProps}
            style={[
                stylesheet.viewport,
                isWeb && orientation === "both" ? stylesheet.bothViewport : undefined
            ]}
            contentContainerStyle={[
                contentContainerStyle,
                isWeb && orientation === "both" ? stylesheet.bothContent : undefined
            ]}
            role={role ?? (ariaLabel ? "region" : undefined)}
            scrollEventThrottle={scrollEventThrottle ?? 16}
            onContentSizeChange={handleContentSizeChange}
            horizontal={orientation === "horizontal"}
            onFocus={interactionHandlers.onFocus}
            onBlur={interactionHandlers.onBlur}
            onLayout={handleLayout}
            onScroll={handleScroll}
            aria-label={ariaLabel}
            ref={scrollRef}
        >
            {children}
        </ScrollView>;
    };

    return <View
        {...webProps}
        style={[
            style,
            stylesheet.container
        ]}
    >
        {renderViewport()}
        {renderFade("top", isVerticalEnabled && edges.isTop)}
        {renderFade("bottom", isVerticalEnabled && edges.isBottom)}
        {renderFade("start", isHorizontalEnabled && edges.isStart)}
        {renderFade("end", isHorizontalEnabled && edges.isEnd)}
        {
            isVerticalBarRendered ?
                <ScrollAreaBar
                    isCornerReserved={isHorizontalBarRendered}
                    contentLength={metrics.contentHeight}
                    onScrollTo={handleVerticalScrollTo}
                    layoutLength={metrics.layoutHeight}
                    getOffset={getVerticalOffset}
                    customTheme={customTheme}
                    isVisible={isBarVisible}
                    offset={verticalOffset}
                    axis="vertical"
                />
            :
                null
        }
        {
            isHorizontalBarRendered ?
                <ScrollAreaBar
                    isCornerReserved={isVerticalBarRendered}
                    onScrollTo={handleHorizontalScrollTo}
                    contentLength={metrics.contentWidth}
                    layoutLength={metrics.layoutWidth}
                    getOffset={getHorizontalOffset}
                    offset={horizontalOffset}
                    customTheme={customTheme}
                    isVisible={isBarVisible}
                    axis="horizontal"
                />
            :
                null
        }
        {renderFocusRing()}
    </View>;
};
export default forwardRef(ScrollArea);
